import React, { useContext, useEffect, useState } from 'react';
import { ModalContext } from '../../providers/Modal/Modal';
import DigitalBankTermsModal from '../DigitalBankTermsModal/DigitalBankTermsModal';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { IDigitalBankTerms, IRate } from '../../types/shared/DigitalBankTerms';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';
import { retrieveDBOsgiUrl, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { Button } from '../../importCL';
export type ProductContent = {
    title?: string;
    rate?: string;
    modalLinkText?: string;
    modalContentFragmentPath?: string;
    ctaText?: string;
    ctaUrl?: string;
    ctaId?: 'Deposit_CompareHYSASubmit' | 'Deposit_CompareJumboSubmit' | 'Deposit_CompareBumpSubmit';
    isOpenNewTab?: boolean;
    subCtaText?: string;
    subCtaUrl?: string;
    isOpenNewTabSubCta?: boolean;
    productTilesContent?: { text?: string; }[];
}

interface IProductTileProps { 
    product: ProductContent, 
    isMobile: boolean, 
    isMetaDataOverride?: boolean;
}

const ProductTile = ({ product, isMobile, isMetaDataOverride }: IProductTileProps): JSX.Element => {
    const { setModalOpen, setModalContent, setModalClass } = useContext(ModalContext);
    const { termRates } = useTermRatesContext();
    const [ rate, setRate ] = useState<string>(product.rate);
    const [ ctaUrl, setCtaUrl ] = useState<string>(product.ctaUrl);

    useEffect(() => {
        const { highYieldSavings, jumboCd, bumpUpCd } = 
            isMetaDataOverride 
                ? JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') || '{}') as IDigitalBankTerms & {apiUrl?: string, baseUrl?: string} 
                : termRates;

        const productMap = {
            'Deposit_CompareHYSASubmit': highYieldSavings,
            'Deposit_CompareJumboSubmit': jumboCd,
            'Deposit_CompareBumpSubmit': bumpUpCd,
        } as const;
        const featuredRate = productMap[product.ctaId as keyof typeof productMap]?.find((r: IRate) => r.featuredRate);
        if (featuredRate) {
            setRate(`${featuredRate.apy}% APY${featuredRate.term && ` (${featuredRate.term} Month${featuredRate.term !== '1' && 's' || ''})` || ''}`);
            setCtaUrl(retrieveDBOsgiUrl(featuredRate.url));
        } else {
            const defaultLowestRate = productMap[product.ctaId as keyof typeof productMap]?.reduce((lowest, current) =>Number(current.apy) < Number(lowest.apy) ? current : lowest);
            if (defaultLowestRate) {
                setRate(`${defaultLowestRate.apy}% APY${defaultLowestRate.term && ` (${defaultLowestRate.term} Month${defaultLowestRate.term !== '1' && 's' || ''})` || ''}`);
                setCtaUrl(retrieveDBOsgiUrl(defaultLowestRate.url));
            }
        }
    }, [termRates]);
    

    const handleModalClick = async () => {
        const termsContentFragmentData = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') || '{}') as IDigitalBankTerms & {apiUrl?: string, baseUrl?: string};
        const termsdata = isMetaDataOverride ? termsContentFragmentData : termRates;
        const termsMap = {
            Deposit_CompareJumboSubmit: {
                term: 'jumboCd' as const,
                parent: 'jumboCdTerm' as const,
            } as const,
            Deposit_CompareBumpSubmit: {
                term: 'bumpUpCd' as const,
                parent: 'bumpCdTerm' as const,
            } as const,
            Deposit_CompareHYSASubmit: {
                term: 'highYieldSavings' as const,
                parent: 'highYieldSavingsTerm' as const,
            } as const,
        } as const;

        if (
            !termsMap[product.ctaId] 
            || !termsdata[termsMap[product.ctaId as keyof typeof termsMap]?.term]
        ) {
            return;
        }
        const mappedTermsData = {
            ...termsContentFragmentData[termsMap[product.ctaId as keyof typeof termsMap]?.parent],
            tableRows: 
                termsdata[termsMap[product.ctaId as keyof typeof termsMap]?.term]
                    .sort((a, b) => parseInt(a?.term ?? '') - parseInt(b?.term ?? ''))
                    .map((product) => ({
                        columnOne: product?.term && product.term !== '0' && `${product.term} Month${product.term !== '1' && 's' || ''}`,
                        columnTwo: product?.apy && `${product.apy}%`,
                        columnThreeText: 'Open an Account',
                        columnThreeLink: retrieveDBOsgiUrl(product.url),
                    })),
        };

        setModalContent(<DigitalBankTermsModal 
            termsData={mappedTermsData} 
            closeCallback={() => setModalOpen(false)} 
        />);
        setModalClass('TileTermsModal');
        setModalOpen(true);
    };

    return (
        <div id='product-tiles' className='ProductTiles__container'>
            <div className='ProductTiles__container--inner'>
                {product.title && <div className='ProductTiles__title'>{transformHTMLParserLinks(product.title)}</div>}
                <div className='ProductTiles__tiles'>
                    <div className='ProductTiles__details'>
                        <div className='ProductTiles__rate'>{rate && transformHTMLParserLinks(rate)}</div>
                        {
                            (product.modalContentFragmentPath) ?
                                <a className='ProductTiles__modalLink' onClick={() => handleModalClick()}>{transformHTMLParserLinks(wrapTrademarkSuperscript(product.modalLinkText))}</a>
                                : <span className='ProductTiles__modalLink ProductTiles__modalLink--none'>{transformHTMLParserLinks(wrapTrademarkSuperscript(product.modalLinkText))}</span>
                        }
                        <hr />
                    </div>
                    {product.productTilesContent && product.productTilesContent.map((content: any, index: number) => (
                        <div key={index} className='ProductTiles__details'>
                            <div className='ProductTiles__detailsContent'>{content.text && transformHTMLParserLinks(wrapTrademarkSuperscript(content.text))}</div>
                            {index !== product.productTilesContent!.length - 1 ? <hr />: <span className='ProductTiles__detailsContent--bottomMargin'></span>}
                        </div>
                    ))}
                </div>
                <div className='ProductTiles__ctaContainer'>
                    <Button className='ProductTiles__cta' size={isMobile ? 'medium' : 'large'} target={product.isOpenNewTab ? '_blank' : '_self'} href={ctaUrl} id={product.ctaId}>
                        <>{product.ctaText && transformHTMLParserLinks(wrapTrademarkSuperscript(product.ctaText))}</>
                    </Button>
                    <Button className='ProductTiles__ctaLink' icon='chevron' size='medium' target={product.isOpenNewTabSubCta ? '_blank' : '_self'} variant='link' href={product.subCtaUrl}>
                        <>{product.subCtaText && transformHTMLParserLinks(wrapTrademarkSuperscript(product.subCtaText))}</>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ProductTile;